import React, { useState, useEffect } from "react";
import {
  Select, DatePicker, Button, Modal, Tag, Avatar,
  Typography, Divider, Empty, Spin, Row, Col,
} from "antd";
import { useDispatch } from "react-redux";
import axios from "axios";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import { toast } from "react-hot-toast";
import moment from "moment";
import Layout from "../../components/layout";
import {
   MedicineBoxOutlined, EnvironmentOutlined,
  CalendarOutlined, ClockCircleOutlined, CheckCircleOutlined,
  ArrowRightOutlined, StarFilled, SearchOutlined,
  TeamOutlined, CloseCircleOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
const { Option } = Select;

/* ─── Tokens ────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Style injection ────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("abk-styles")) return;
  const el = document.createElement("style");
  el.id = "abk-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .abk-root { font-family: 'DM Sans', sans-serif; background: ${SURFACE}; min-height: 100vh; }
    .abk-wrap { max-width: 1100px; margin: 0 auto; padding: 32px 24px 72px; }

    /* Hero */
    .abk-hero {
      background: linear-gradient(135deg, ${SLATE} 0%, ${SLATE_MID} 100%);
      border-radius: 24px; padding: 36px 44px;
      position: relative; overflow: hidden; margin-bottom: 32px;
    }
    .abk-hero::before {
      content: ''; position: absolute; top: -80px; right: -80px;
      width: 300px; height: 300px; border-radius: 50%;
      background: radial-gradient(circle, ${TEAL}44 0%, transparent 70%);
      filter: blur(32px); pointer-events: none;
    }
    .abk-hero::after {
      content: ''; position: absolute; bottom: -60px; left: -40px;
      width: 200px; height: 200px; border-radius: 50%;
      background: radial-gradient(circle, ${EMERALD}33 0%, transparent 70%);
      filter: blur(26px); pointer-events: none;
    }
    .abk-eyebrow {
      display: inline-block; padding: 4px 12px; border-radius: 20px;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase;
      font-family: 'Sora', sans-serif; margin-bottom: 12px;
    }

    /* Step strip */
    .abk-steps {
      display: flex; gap: 0; margin-bottom: 32px;
      background: ${WHITE}; border-radius: 16px;
      box-shadow: 0 4px 20px rgba(0,0,0,.06);
      overflow: hidden;
    }
    .abk-step {
      flex: 1; padding: 18px 24px;
      display: flex; align-items: center; gap: 12px;
      border-right: 1px solid #f1f5f9; position: relative;
    }
    .abk-step:last-child { border-right: none; }
    .abk-step-num {
      width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
      font-size: 13px; font-weight: 700; font-family: 'Sora', sans-serif;
      background: #f1f5f9; color: ${MUTED};
      transition: background .3s, color .3s;
    }
    .abk-step.active .abk-step-num { background: ${TEAL}; color: ${WHITE}; }
    .abk-step.done .abk-step-num   { background: #22c55e; color: ${WHITE}; }
    .abk-step-label { font-size: 13px; font-weight: 600; color: ${MUTED}; transition: color .3s; }
    .abk-step.active .abk-step-label,
    .abk-step.done .abk-step-label { color: ${SLATE_MID}; }
    .abk-step-sub { font-size: 11px; color: ${MUTED}; margin-top: 1px; }

    /* Patient selector card */
    .abk-selector-card {
      background: ${WHITE}; border-radius: 20px;
      box-shadow: 0 4px 20px rgba(0,0,0,.06);
      padding: 28px 28px 24px; margin-bottom: 28px;
    }
    .abk-card-head {
      display: flex; align-items: center; gap: 10px;
      margin-bottom: 20px; padding-bottom: 16px;
      border-bottom: 1.5px solid #f1f5f9;
    }
    .abk-card-icon {
      width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      display: flex; align-items: center; justify-content: center; font-size: 16px;
    }

    /* Patient select antd override */
    .abk-root .ant-select-selector {
      border-radius: 12px !important; height: 50px !important;
      border: 1.5px solid #e2e8f0 !important; padding: 0 16px !important;
      display: flex !important; align-items: center !important;
      font-family: 'DM Sans', sans-serif !important; font-size: 14px !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .abk-root .ant-select-selection-item,
    .abk-root .ant-select-selection-placeholder { line-height: 48px !important; }
    .abk-root .ant-select-focused .ant-select-selector,
    .abk-root .ant-select-selector:hover {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }
    .abk-root .ant-select-selection-search { display: flex; align-items: center; }
    .abk-root .ant-select-selection-search-input { height: 48px !important; }

    /* Selected patient pill */
    .abk-patient-pill {
      display: inline-flex; align-items: center; gap: 10px;
      background: ${TEAL_LIGHT}; border: 1.5px solid ${TEAL}44;
      border-radius: 12px; padding: 10px 16px; margin-top: 14px;
    }
    .abk-patient-pill-avatar {
      width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
      background: linear-gradient(135deg, ${EMERALD}, ${TEAL});
      display: flex; align-items: center; justify-content: center;
      color: ${WHITE}; font-size: 13px; font-weight: 700;
    }

    /* Doctor cards grid */
    .abk-doctor-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 20px; }

    .abk-doctor-card {
      background: ${WHITE}; border-radius: 20px;
      border: 1.5px solid #f1f5f9;
      box-shadow: 0 4px 16px rgba(0,0,0,.05);
      padding: 24px; cursor: pointer;
      transition: transform .25s, box-shadow .25s, border-color .25s;
      position: relative; overflow: hidden;
    }
    .abk-doctor-card::before {
      content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
      background: linear-gradient(90deg, ${EMERALD}, ${TEAL});
      transform: scaleX(0); transform-origin: left;
      transition: transform .3s ease;
    }
    .abk-doctor-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 16px 40px rgba(13,148,136,.15);
      border-color: ${TEAL}55;
    }
    .abk-doctor-card:hover::before { transform: scaleX(1); }

    .abk-doc-avatar {
      width: 56px; height: 56px; border-radius: 16px;
      background: linear-gradient(135deg, ${EMERALD}, ${TEAL});
      display: flex; align-items: center; justify-content: center;
      font-size: 22px; font-weight: 800; color: ${WHITE};
      font-family: 'Sora', sans-serif;
      box-shadow: 0 6px 18px ${TEAL}44; margin-bottom: 14px;
    }
    .abk-doc-tag {
      background: ${TEAL_LIGHT} !important; color: ${EMERALD} !important;
      border: 1px solid ${TEAL}33 !important;
      border-radius: 20px !important; font-size: 11px !important;
      font-weight: 600 !important; padding: 2px 10px !important;
    }
    .abk-doc-meta {
      display: flex; align-items: center; gap: 6px;
      font-size: 12px; color: ${MUTED}; margin-top: 8px;
    }
    .abk-doc-meta-icon { color: ${TEAL}; font-size: 12px; }
    .abk-book-hint {
      margin-top: 16px; padding-top: 14px;
      border-top: 1px solid #f1f5f9;
      display: flex; align-items: center; justify-content: space-between;
    }
    .abk-slot-badge {
      display: inline-flex; align-items: center; gap: 4px;
      background: #f0fdf4; border: 1px solid #bbf7d0;
      border-radius: 20px; padding: 3px 10px;
      font-size: 11px; font-weight: 600; color: #16a34a;
    }

    /* Day chips */
    .abk-day-chip {
      display: inline-block; padding: 2px 8px; border-radius: 20px;
      background: rgba(20,184,166,.12); color: ${TEAL};
      font-size: 11px; font-weight: 600;
      border: 1px solid rgba(20,184,166,.25);
    }

    /* Modal overrides */
    .abk-modal .ant-modal-content {
      border-radius: 24px !important; overflow: hidden;
      padding: 0 !important;
    }
    .abk-modal .ant-modal-body { padding: 0 !important; }
    .abk-modal .ant-modal-close {
      top: 16px !important; right: 16px !important;
      color: ${WHITE} !important;
    }
    .abk-modal-left {
      background: linear-gradient(160deg, ${SLATE} 0%, ${SLATE_MID} 100%);
      padding: 36px 32px; position: relative; overflow: hidden;
    }
    .abk-modal-left::before {
      content: ''; position: absolute; top: -60px; right: -60px;
      width: 220px; height: 220px; border-radius: 50%;
      background: radial-gradient(circle, ${TEAL}44 0%, transparent 70%);
      filter: blur(24px); pointer-events: none;
    }
    .abk-modal-right { padding: 36px 32px; background: ${WHITE}; }

    /* Modal info row */
    .abk-m-row {
      display: flex; align-items: flex-start; gap: 10px;
      padding: 10px 0; border-bottom: 1px solid rgba(255,255,255,.07);
    }
    .abk-m-row:last-child { border-bottom: none; }
    .abk-m-icon {
      width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0;
      background: rgba(20,184,166,.15);
      display: flex; align-items: center; justify-content: center;
      color: ${TEAL}; font-size: 13px;
    }

    /* Date picker inside modal */
    .abk-modal .ant-picker {
      width: 100%; border-radius: 12px; height: 48px;
      border: 1.5px solid #e2e8f0;
      font-family: 'DM Sans', sans-serif; font-size: 14px;
      transition: border-color .2s, box-shadow .2s;
    }
    .abk-modal .ant-picker:hover,
    .abk-modal .ant-picker-focused {
      border-color: ${TEAL} !important;
      box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }

    /* Buttons */
    .abk-btn-check {
      height: 48px !important; width: 100% !important; border-radius: 12px !important;
      font-size: 14px !important; font-weight: 700 !important;
      font-family: 'Sora', sans-serif !important; border: none !important;
      background: linear-gradient(135deg, ${EMERALD}, ${TEAL}) !important;
      color: ${WHITE} !important;
      box-shadow: 0 6px 18px ${TEAL}44 !important;
      transition: transform .2s, box-shadow .2s !important;
    }
    .abk-btn-check:hover { transform: translateY(-2px) !important; box-shadow: 0 12px 28px ${TEAL}55 !important; }
    .abk-btn-book {
      height: 48px !important; width: 100% !important; border-radius: 12px !important;
      font-size: 14px !important; font-weight: 700 !important;
      font-family: 'Sora', sans-serif !important; border: none !important;
      background: linear-gradient(135deg, #16a34a, #22c55e) !important;
      color: ${WHITE} !important;
      box-shadow: 0 6px 18px #22c55e44 !important;
      transition: transform .2s, box-shadow .2s !important;
    }
    .abk-btn-book:hover { transform: translateY(-2px) !important; }

    .abk-available-pill {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 8px 14px; border-radius: 20px;
      background: #dcfce7; color: #16a34a;
      font-size: 13px; font-weight: 600; margin-bottom: 14px;
    }

    /* Animations */
    @keyframes abkFadeUp {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .abk-fade   { animation: abkFadeUp .5s ease both; }
    .abk-fade-1 { animation-delay: .05s; }
    .abk-fade-2 { animation-delay: .12s; }
    .abk-fade-3 { animation-delay: .20s; }
    .abk-fade-4 { animation-delay: .28s; }

    @keyframes abkCardIn {
      from { opacity: 0; transform: translateY(20px) scale(.97); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }
    .abk-card-anim { animation: abkCardIn .4s ease both; }

    @media (max-width: 768px) {
      .abk-hero { padding: 28px 24px; }
      .abk-steps { flex-direction: column; }
      .abk-step { border-right: none; border-bottom: 1px solid #f1f5f9; }
      .abk-step:last-child { border-bottom: none; }
      .abk-doctor-grid { grid-template-columns: 1fr; }
    }
  `;
  document.head.appendChild(el);
};

/* ─── Helpers ────────────────────────────────────────────────── */
const stepState = (current, idx) => {
  if (current > idx) return "done";
  if (current === idx) return "active";
  return "";
};

/* ─── Main Component ──────────────────────────────────────────── */
function AdminBookAppointment() {
  const dispatch = useDispatch();

  // Data
  const [patients, setPatients]         = useState([]);
  const [doctors, setDoctors]           = useState([]);

  // Selection state
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedDoctor, setSelectedDoctor]   = useState(null);

  // Booking state
  const [modalOpen, setModalOpen]   = useState(false);
  const [date, setDate]             = useState(null);
  const [isAvailable, setIsAvailable] = useState(false);
  const [availableSlots, setAvailableSlots] = useState(0);
  const [loadingDoctors, setLoadingDoctors] = useState(false);

  // Current step: 1 = pick patient, 2 = pick doctor, 3 = modal open
  const currentStep = !selectedPatient ? 1 : !selectedDoctor ? 2 : 3;

  useEffect(() => { injectStyles(); }, []);

  /* Fetch patients */
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const res = await axios.get("/api/admin/get-patients", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (res.data.success) setPatients(res.data.data);
      } catch { toast.error("Could not load patients"); }
    };
    fetchPatients();
  }, []);

  /* Fetch doctors whenever a patient is selected */
  useEffect(() => {
    if (!selectedPatient) return;
    const fetchDoctors = async () => {
      setLoadingDoctors(true);
      try {
        const res = await axios.get("/api/user/get-all-approved-doctors", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (res.data.success) setDoctors(res.data.data);
      } catch { toast.error("Could not load doctors"); }
      finally { setLoadingDoctors(false); }
    };
    fetchDoctors();
  }, [selectedPatient]);

  /* Open modal for a doctor */
  const openDoctorModal = (doc) => {
    setSelectedDoctor(doc);
    setDate(null);
    setIsAvailable(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedDoctor(null);
    setDate(null);
    setIsAvailable(false);
  };

  /* Check availability */
  const checkAvailability = async () => {
    if (!date) { toast.error("Please select a date first."); return; }
    try {
      dispatch(showLoading());
      const res = await axios.post(
        "/api/bookAppointment/check-availability",
        { doctorId: selectedDoctor._id, date, time: "09:00" },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (res.data.success) {
        toast.success(res.data.message);
        setIsAvailable(true);
        setAvailableSlots(res.data.availableSlots);
      } else {
        toast.error(res.data.message);
        setIsAvailable(false);
      }
    } catch {
      dispatch(hideLoading());
      toast.error("Error checking availability");
    }
  };

  /* Book appointment */
const bookAppointment = async () => {
  if (!date || !isAvailable) {
    toast.error("Please check availability first.");
    return;
  }

  try {
    dispatch(showLoading());

    const res = await axios.post(
      "/api/admin/book-appointment-for-patient",
      {
        patientId: selectedPatient._id,
        doctorId: selectedDoctor._id,
        doctorInfo: selectedDoctor,
        userInfo: selectedPatient,
        date,
      },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    dispatch(hideLoading());

    if (res.data.success) {
      toast.success(res.data.message);

      closeModal();
      setSelectedPatient(null);
      setDoctors([]);
    } else {
      toast.error(
        res.data.message ||
          "Unable to book appointment at the moment."
      );
    }
  } catch (error) {
    dispatch(hideLoading());

    const errorMessage =
      error?.response?.data?.message ||
      "Unable to complete appointment booking.";

    toast.error(errorMessage);
  }
};


  return (
    <Layout>
      <div className="abk-root">
        <div className="abk-wrap">

          {/* ── Hero ── */}
          <div className="abk-hero abk-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="abk-eyebrow">
                <CalendarOutlined style={{ marginRight: 5 }} />Appointment Management
              </div>
              <Title level={2} style={{ margin: "6px 0 4px", fontFamily: "'Sora', sans-serif", color: WHITE, fontWeight: 800 }}>
                Book Patient Appointment
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                Select a patient, choose a doctor, and schedule their visit in one flow
              </Text>
            </div>
          </div>

          {/* ── Steps ── */}
          <div className="abk-steps abk-fade abk-fade-1">
            {[
              { num: 1, label: "Select Patient",  sub: "Search by name or phone" },
              { num: 2, label: "Choose Doctor",   sub: "Browse available doctors" },
              { num: 3, label: "Book Appointment", sub: "Pick date & confirm" },
            ].map((s) => (
              <div key={s.num} className={`abk-step ${stepState(currentStep, s.num)}`}>
                <div className="abk-step-num">
                  {currentStep > s.num
                    ? <CheckCircleOutlined style={{ fontSize: 14 }} />
                    : s.num}
                </div>
                <div>
                  <div className="abk-step-label">{s.label}</div>
                  <div className="abk-step-sub">{s.sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Step 1: Patient Selector ── */}
          <div className="abk-selector-card abk-fade abk-fade-2">
            <div className="abk-card-head">
              <div className="abk-card-icon"><TeamOutlined /></div>
              <div>
                <Title level={5} style={{ margin: 0, fontFamily: "'Sora', sans-serif", color: SLATE, fontWeight: 700 }}>
                  Select Patient
                </Title>
                <Text style={{ color: MUTED, fontSize: 12 }}>Search by name, email or phone number</Text>
              </div>
            </div>

            <Select
              showSearch
              style={{ width: "100%" }}
              placeholder={
                <span style={{ display: "flex", alignItems: "center", gap: 8, color: MUTED }}>
                  <SearchOutlined /> Search patients…
                </span>
              }
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              value={selectedPatient?._id || undefined}
              onChange={(val) => {
                const p = patients.find(p => p._id === val);
                setSelectedPatient(p || null);
                setDoctors([]);
                setIsAvailable(false);
              }}
              optionLabelProp="label"
              allowClear
              onClear={() => { setSelectedPatient(null); setDoctors([]); }}
            >
              {patients.map(p => (
                <Option key={p._id} value={p._id} label={`${p.name} · ${p.phone}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0" }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: "50%",
                      background: `linear-gradient(135deg, ${EMERALD}, ${TEAL})`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: WHITE, fontSize: 12, fontWeight: 700, flexShrink: 0,
                    }}>
                      {p.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: SLATE_MID }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: MUTED }}>{p.email} · {p.phone}</div>
                    </div>
                  </div>
                </Option>
              ))}
            </Select>

            {/* Selected patient pill */}
            {selectedPatient && (
              <div className="abk-patient-pill" style={{ marginTop: 14 }}>
                <div className="abk-patient-pill-avatar">
                  {selectedPatient.name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: SLATE_MID }}>{selectedPatient.name}</div>
                  <div style={{ fontSize: 11, color: EMERALD }}>{selectedPatient.email} · {selectedPatient.phone}</div>
                </div>
                <button
                  onClick={() => { setSelectedPatient(null); setDoctors([]); }}
                  style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: MUTED, fontSize: 16 }}
                >
                  <CloseCircleOutlined />
                </button>
              </div>
            )}
          </div>

          {/* ── Step 2: Doctor Grid ── */}
          {selectedPatient && (
            <div className="abk-fade abk-fade-3">
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                <div className="abk-card-icon"><MedicineBoxOutlined /></div>
                <div>
                  <Title level={5} style={{ margin: 0, fontFamily: "'Sora', sans-serif", color: SLATE, fontWeight: 700 }}>
                    Available Doctors
                  </Title>
                  <Text style={{ color: MUTED, fontSize: 12 }}>Click a doctor to open the booking form</Text>
                </div>
              </div>

              {loadingDoctors ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                  <Spin size="large" style={{ color: TEAL }} />
                  <div style={{ color: MUTED, marginTop: 12, fontSize: 13 }}>Loading doctors…</div>
                </div>
              ) : doctors.length === 0 ? (
                <Empty description={<span style={{ color: MUTED }}>No doctors available</span>} />
              ) : (
                <div className="abk-doctor-grid">
                  {doctors.map((doc, i) => (
                    <div
                      key={doc._id}
                      className="abk-doctor-card abk-card-anim"
                      style={{ animationDelay: `${i * 0.06}s` }}
                      onClick={() => openDoctorModal(doc)}
                    >
                      {/* Avatar */}
                      <div className="abk-doc-avatar">
                        {doc.firstName?.[0]}
                      </div>

                      {/* Name + tag */}
                      <Title level={5} style={{ margin: "0 0 4px", fontFamily: "'Sora', sans-serif", color: SLATE, fontWeight: 700 }}>
                        Dr. {doc.firstName} {doc.lastName}
                      </Title>
                      <Tag className="abk-doc-tag">{doc.specialization || doc.service || "Specialist"}</Tag>

                      {/* Meta */}
                      <div className="abk-doc-meta" style={{ marginTop: 10 }}>
                        <MedicineBoxOutlined className="abk-doc-meta-icon" />
                        <span>{doc.service}</span>
                      </div>
                      <div className="abk-doc-meta">
                        <EnvironmentOutlined className="abk-doc-meta-icon" />
                        <span>{doc.location}</span>
                      </div>
                      <div className="abk-doc-meta">
                        <ClockCircleOutlined className="abk-doc-meta-icon" />
                        <span>{doc.appointmentTime?.[0]} – {doc.appointmentTime?.[1]}</span>
                      </div>

                      {/* Days */}
                      {doc.clinicDay?.length > 0 && (
                        <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {doc.clinicDay.map((d, j) => (
                            <span key={j} className="abk-day-chip">{d}</span>
                          ))}
                        </div>
                      )}

                      {/* Footer */}
                      <div className="abk-book-hint">
                        <div className="abk-slot-badge">
                          <CheckCircleOutlined style={{ fontSize: 10 }} />
                          {doc.availableSlots ?? "–"} slots available
                        </div>
                        <div style={{ fontSize: 12, color: TEAL, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                          Book <ArrowRightOutlined style={{ fontSize: 10 }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Booking Modal ── */}
      <Modal
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        width={760}
        className="abk-modal"
        destroyOnClose
        centered
      >
        {selectedDoctor && (
          <Row style={{ minHeight: 480 }}>

            {/* Left: Doctor details */}
            <Col xs={24} md={11}>
              <div className="abk-modal-left" style={{ height: "100%" }}>
                <div style={{ position: "relative", zIndex: 1 }}>

                  {/* Avatar + name */}
                  <Avatar
                    size={64}
                    style={{
                      background: `linear-gradient(135deg, ${EMERALD}, ${TEAL})`,
                      fontSize: 26, fontWeight: 800, fontFamily: "'Sora', sans-serif",
                      boxShadow: `0 8px 24px ${TEAL}55`, marginBottom: 12,
                      display: "flex",
                    }}
                  >
                    {selectedDoctor.firstName?.[0]}
                  </Avatar>

                  <Title level={4} style={{ margin: "0 0 4px", color: WHITE, fontFamily: "'Sora', sans-serif", fontWeight: 700 }}>
                    Dr. {selectedDoctor.firstName} {selectedDoctor.lastName}
                  </Title>
                  <Tag style={{ background: `${TEAL}33`, color: "#5eead4", border: `1px solid ${TEAL}44`, borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                    {selectedDoctor.specialization || selectedDoctor.service}
                  </Tag>

                  <Divider style={{ borderColor: "rgba(255,255,255,.1)", margin: "20px 0 14px" }} />

                  {/* Info rows */}
                  <div className="abk-m-row">
                    <div className="abk-m-icon"><MedicineBoxOutlined /></div>
                    <div>
                      <Text style={{ color: MUTED, fontSize: 10, display: "block", fontWeight: 600 }}>SERVICE</Text>
                      <Text style={{ color: WHITE, fontSize: 13 }}>{selectedDoctor.service}</Text>
                    </div>
                  </div>

                  <div className="abk-m-row">
                    <div className="abk-m-icon"><EnvironmentOutlined /></div>
                    <div>
                      <Text style={{ color: MUTED, fontSize: 10, display: "block", fontWeight: 600 }}>LOCATION</Text>
                      <Text style={{ color: WHITE, fontSize: 13 }}>{selectedDoctor.location}</Text>
                    </div>
                  </div>

                  <div className="abk-m-row">
                    <div className="abk-m-icon"><ClockCircleOutlined /></div>
                    <div>
                      <Text style={{ color: MUTED, fontSize: 10, display: "block", fontWeight: 600 }}>HOURS</Text>
                      <Text style={{ color: WHITE, fontSize: 13 }}>
                        {selectedDoctor.appointmentTime?.[0]} – {selectedDoctor.appointmentTime?.[1]}
                      </Text>
                    </div>
                  </div>

                  <div className="abk-m-row" style={{ flexDirection: "column", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div className="abk-m-icon"><CalendarOutlined /></div>
                      <div>
                        <Text style={{ color: MUTED, fontSize: 10, display: "block", fontWeight: 600 }}>CLINIC DAYS</Text>
                      </div>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, paddingLeft: 42 }}>
                      {selectedDoctor.clinicDay?.map((d, i) => (
                        <span key={i} className="abk-day-chip">{d}</span>
                      ))}
                    </div>
                  </div>

                  {/* Stars */}
                  <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 4 }}>
                    {[...Array(5)].map((_, i) => (
                      <StarFilled key={i} style={{ color: "#fbbf24", fontSize: 13 }} />
                    ))}
                    <Text style={{ color: MUTED, fontSize: 11, marginLeft: 4 }}>Verified Doctor</Text>
                  </div>

                  {/* Booking for patient pill */}
                  <div style={{ marginTop: 20, background: "rgba(255,255,255,.07)", borderRadius: 10, padding: "10px 14px" }}>
                    <Text style={{ color: MUTED, fontSize: 10, display: "block", fontWeight: 600, marginBottom: 4 }}>BOOKING FOR</Text>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{
                        width: 26, height: 26, borderRadius: "50%",
                        background: `linear-gradient(135deg, ${EMERALD}, ${TEAL})`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: WHITE, fontSize: 11, fontWeight: 700,
                      }}>
                        {selectedPatient?.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <Text style={{ color: WHITE, fontSize: 13, fontWeight: 600 }}>{selectedPatient?.name}</Text>
                        <Text style={{ color: MUTED, fontSize: 11, display: "block" }}>{selectedPatient?.phone}</Text>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            {/* Right: Booking form */}
            <Col xs={24} md={13}>
              <div className="abk-modal-right" style={{ height: "100%" }}>
                <Title level={4} style={{ margin: "0 0 4px", fontFamily: "'Sora', sans-serif", color: SLATE, fontWeight: 700 }}>
                  Schedule Appointment
                </Title>
                <Text style={{ color: MUTED, fontSize: 13 }}>
                  Pick a date within the doctor's schedule
                </Text>

                <Divider style={{ margin: "20px 0" }} />

                {/* Date picker */}
                <div style={{ marginBottom: 24 }}>
                  <Text strong style={{ color: SLATE_MID, fontSize: 13, display: "block", marginBottom: 10 }}>
                    <CalendarOutlined style={{ marginRight: 6, color: EMERALD }} />
                    Appointment Date
                  </Text>
                  <DatePicker
                    format="DD-MM-YYYY"
                    style={{ width: "100%" }}
                    placeholder="Select appointment date"
                    disabledDate={current => current && current < moment().startOf("day")}
                    onChange={(val) => {
                      setDate(val ? moment(val).format("DD-MM-YYYY") : null);
                      setIsAvailable(false);
                    }}
                  />
                </div>

                {/* Available pill */}
                {isAvailable && (
                  <div className="abk-available-pill">
                    <CheckCircleOutlined />
                    {availableSlots} slot{availableSlots !== 1 ? "s" : ""} available — ready to book!
                  </div>
                )}

                {/* Slot info */}
                {!isAvailable && (
                  <div style={{
                    background: TEAL_LIGHT, borderRadius: 12, padding: "12px 16px",
                    marginBottom: 20, display: "flex", alignItems: "flex-start", gap: 10,
                  }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: EMERALD, flexShrink: 0, marginTop: 5 }} />
                    <Text style={{ color: EMERALD, fontSize: 12, lineHeight: 1.5 }}>
                      Select a date and click "Check Availability" to confirm the slot before booking.
                    </Text>
                  </div>
                )}

                {/* Action button */}
                {!isAvailable ? (
                  <Button
                    className="abk-btn-check"
                    disabled={!date}
                    onClick={checkAvailability}
                    icon={<ClockCircleOutlined />}
                  >
                    Check Availability
                  </Button>
                ) : (
                  <Button
                    className="abk-btn-book"
                    onClick={bookAppointment}
                    icon={<ArrowRightOutlined />}
                  >
                    Confirm Booking
                  </Button>
                )}

                {isAvailable && (
                  <button
                    onClick={() => setIsAvailable(false)}
                    style={{
                      display: "block", width: "100%", marginTop: 12,
                      background: "none", border: "none", cursor: "pointer",
                      color: MUTED, fontSize: 12, textAlign: "center",
                    }}
                  >
                    ← Change date
                  </button>
                )}

                <Text style={{ color: MUTED, fontSize: 11, marginTop: 20, display: "block", textAlign: "center" }}>
                  Patient will receive a WhatsApp & email confirmation once booked
                </Text>
              </div>
            </Col>
          </Row>
        )}
      </Modal>
    </Layout>
  );
}

export default AdminBookAppointment;